import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { Autocomplete } from '@mui/material'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormHelperText from '@mui/material/FormHelperText'
import MuiTextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Query } from '@promoboxx/graphql-gateway-types'
import { Field, Form, Formik } from 'formik'
import { CheckboxWithLabel, TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'
import * as Yup from 'yup'

import useDebounceValue from '@src/hooks/useDebounceValue'

const CREATE_BRAND_REQUEST = gql`
  mutation createBrandRequest($data: CreateBrandRequestBrandRequest!) {
    createBrandRequest(brand_request: $data) {
      id
    }
  }
`

const GET_BRANDS = gql`
  query getBrands(
    $filters: GetBrandsFilters
    $sort_column: String
    $sort_direction: String
  ) {
    getBrands(
      filters: $filters
      sort_column: $sort_column
      sort_direction: $sort_direction
    ) {
      name
      id
    }
  }
`

const useStyles = makeStyles()((theme) => ({
  fieldSpacer: {
    paddingBottom: theme.spacing(2),
  },
  ccMe: {
    paddingBottom: theme.spacing(3),
  },
}))

const urlRegexp =
  // eslint-disable-next-line no-useless-escape
  /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\%text'\(\)\*\+,;=.&]+$/i

const schema = Yup.object().shape({
  website: Yup.string()
    .required(I18n.t('manageBrands.suggestBrandsPage.websiteNotEntered'))
    .matches(
      urlRegexp,
      I18n.t('manageBrands.suggestBrandsPage.websiteNotEntered'),
    ),
})

const alreadyRequestedBrand = (graphQLErrors?: any[]) => {
  if (!graphQLErrors) {
    return false
  }
  for (let error of graphQLErrors) {
    if (
      error?.extensions?.response?.body?.errors?.includes(
        'Retailer account already has a brand request with this brand',
      )
    ) {
      return true
    }
  }
  return false
}

const alreadyConnectedToBrand = (graphQLErrors?: any[]) => {
  if (!graphQLErrors) {
    return false
  }
  for (let error of graphQLErrors) {
    if (
      error?.extensions?.response?.body?.errors?.includes(
        'Retailer account already has a retailer with this brand',
      )
    ) {
      return true
    }
  }
  return false
}

export interface SuggestBrandsFormProps {
  onDidSubmit?: () => void
}

interface BrandOption {
  name: string
  id: string
}

const SuggestBrandsForm: React.FC<
  React.PropsWithChildren<SuggestBrandsFormProps>
> = (props) => {
  const [createBrandRequest] = useMutation<Query>(CREATE_BRAND_REQUEST)
  const [runQuery, { data, loading }] = useLazyQuery<Query>(GET_BRANDS)
  const [autocompleteOpen, setAutocompleteOpen] = useState(false)
  const [brandName, setBrandName] = useState('')
  const [selectedBrand, setSelectedBrand] = useState<BrandOption | null>(null)
  const debounced = useDebounceValue(brandName, 500)

  const brandList: BrandOption[] = []
  data?.getBrands?.forEach((brand) => {
    if (brand.name !== undefined && brand.name !== null) {
      brandList.push({
        name: brand.name,
        id: brand.id,
      })
    }
  })
  const customBrand: BrandOption = {
    name: 'Custom',
    id: '-1',
  }
  if (brandName.length >= 3) {
    if (brandList.length !== 1) {
      brandList.push(customBrand)
    } else if (
      brandList.length === 1 &&
      brandName.toLowerCase() !== brandList[0]?.name.toLowerCase()
    ) {
      brandList.push(customBrand)
    }
  }

  const retailerAccount = useSelector(
    (state: any) => state.app.currentRetailerAccount,
  )

  useEffect(() => {
    if (debounced.length < 3) {
      return
    }
    runQuery({
      variables: {
        filters: {
          search: debounced,
          brand_requests_enabled: true,
          demo: false,
          community: false,
        },
        sort_column: 'name',
        sort_direction: 'ASC',
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  const { enqueueSnackbar } = useSnackbar()

  const { classes } = useStyles()

  return (
    <Formik
      initialValues={{ website: '', followUp: true, messageBody: '' }}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true)

        let brandData = {
          brand_id: selectedBrand?.id,
          brand_name: selectedBrand?.name,
        }
        if (selectedBrand?.id === '-1') {
          brandData = {
            brand_id: undefined,
            brand_name: brandName, //The entered searched term
          }
        }

        const data = {
          retailer_account_id: retailerAccount,
          website: values.website,
          follow_up: values.followUp,
          retailer_account_message: values.messageBody,
          source: 'suggested',
          ...brandData,
        }

        await createBrandRequest({
          variables: {
            data,
          },
        })
          .then(() => {
            setBrandName('')
            actions.resetForm()
            props.onDidSubmit?.()
          })
          .catch((err) => {
            if (alreadyRequestedBrand(err.graphQLErrors)) {
              enqueueSnackbar(
                I18n.t('manageBrands.suggestBrandsPage.alreadySubmittedBrand'),
                { variant: 'error' },
              )
            } else if (alreadyConnectedToBrand(err.graphQLErrors)) {
              enqueueSnackbar(
                I18n.t(
                  'manageBrands.suggestBrandsPage.alreadyConnectedToBrand',
                ),
                { variant: 'error' },
              )
            } else {
              enqueueSnackbar(
                I18n.t('manageBrands.suggestBrandsPage.errorMessage'),
                { variant: 'error' },
              )
            }
            setBrandName('')
            actions.setSubmitting(false)
          })
      }}
      validationSchema={
        !selectedBrand || selectedBrand?.id === '-1' ? schema : undefined
      }
    >
      {({ isSubmitting }) => {
        return (
          <Form>
            <Autocomplete
              inputValue={brandName}
              getOptionLabel={(option: BrandOption) => {
                if (option.id === '-1') {
                  return `Suggest this brand: "${brandName}"`
                } else {
                  return option.name
                }
              }}
              noOptionsText={'Please enter at least 3 characters'}
              isOptionEqualToValue={(option: BrandOption, value: BrandOption) =>
                option.id === value.id
              }
              loading={loading}
              onChange={(
                event,
                option: string | BrandOption | null,
                reason,
              ) => {
                if (!option) {
                  setSelectedBrand(null)
                  return
                }
                if (typeof option === 'string') {
                  return
                }
                if (reason === 'clear') {
                  setSelectedBrand(null)
                } else {
                  // Need setBrandName here so that it changes the input to the brand that's been clicked on
                  if (option.id !== '-1') {
                    setBrandName(option.name)
                  }
                  setSelectedBrand(option)
                }
              }}
              onClose={() => {
                setAutocompleteOpen(false)
              }}
              onOpen={() => {
                setAutocompleteOpen(true)
              }}
              open={autocompleteOpen}
              options={brandList.length ? brandList : []}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        <div onClick={() => setBrandName('')}>
                          {params.InputProps.endAdornment}
                        </div>
                      </>
                    ),
                  }}
                  label={I18n.t(
                    'manageBrands.suggestBrandsPage.enterBrandName',
                  )}
                  name="brandName"
                  onChange={(event) => {
                    setBrandName(event.target.value)
                    setSelectedBrand(null)
                  }}
                />
              )}
            />
            {selectedBrand && (
              <div className={classes.ccMe}>
                <Field
                  component={CheckboxWithLabel}
                  Label={{
                    label: I18n.t('manageBrands.suggestBrandsPage.ccMe'),
                  }}
                  name="followUp"
                />
              </div>
            )}
            {selectedBrand && selectedBrand.id === '-1' && (
              <>
                <Typography variant="h6">
                  {I18n.t('manageBrands.suggestBrandsPage.brandWebsite')}
                </Typography>
                <Typography variant="caption">
                  {I18n.t(
                    'manageBrands.suggestBrandsPage.brandWebsiteExplanation',
                  )}
                </Typography>
                <Field
                  className={classes.fieldSpacer}
                  component={TextField}
                  fullWidth
                  label={'https://'}
                  name="website"
                />
                <Typography variant="h6">
                  {I18n.t(
                    'manageBrands.suggestBrandsPage.whyDoYouWantThisBrand',
                  )}
                </Typography>
                <Field
                  component={TextField}
                  fullWidth
                  label={I18n.t(
                    'manageBrands.suggestBrandsPage.whyDoYouWantThisBrand',
                  )}
                  multiline
                  name="messageBody"
                  rows={4}
                />
              </>
            )}
            <Button
              color="primary"
              disabled={isSubmitting || !selectedBrand}
              type="submit"
              variant="contained"
            >
              {I18n.t('manageBrands.suggestBrandsPage.suggestThisBrand')}
            </Button>
            <FormHelperText>
              {I18n.t('manageBrands.informationDisclosureMessage', {
                buttonLabel: I18n.t(
                  'manageBrands.suggestBrandsPage.suggestThisBrand',
                ),
              })}
            </FormHelperText>
          </Form>
        )
      }}
    </Formik>
  )
}

export default SuggestBrandsForm
